import { useState, useCallback, useMemo } from 'react';

export interface UseBulkSelectionReturn {
  selectedIds: string[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  allSelected: (ids: string[]) => boolean;
  count: number;
}

export const useBulkSelection = (): UseBulkSelectionReturn => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelected((prev) => {
      const allCurrentlySelected = ids.every((id) => prev.has(id));
      if (allCurrentlySelected) {
        // Deselect all visible
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      } else {
        // Select all visible
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      }
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => selected.has(id),
    [selected]
  );

  const allSelected = useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => selected.has(id)),
    [selected]
  );

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return {
    selectedIds,
    isSelected,
    toggle,
    selectAll,
    clearSelection,
    allSelected,
    count: selected.size,
  };
};
